import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Loader2, Sparkles, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ResumeUploader from "@/components/ResumeUploader";
import Breadcrumb from "@/components/Breadcrumb";
import { submitCandidate } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(7, "Please enter a valid phone number"),
  location: z.string().optional(),
  current_role: z.string().optional(),
  experience_years: z.string().optional(),
  expected_ctc: z.string().optional(),
  skills: z.string().optional(),
  summary: z.string().optional(),
});

const PERKS = [
  { icon: Sparkles, title: "Curated matches", text: "Hand-picked openings aligned to your profile and goals." },
  { icon: ShieldCheck, title: "Confidential & secure", text: "Your details are never shared without your consent." },
  { icon: Zap, title: "Fast response", text: "Most candidates hear from us within 3 business days." },
];

export default function JoinTalentPoolPage() {
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register, handleSubmit, reset, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "", email: "", phone: "", location: "", current_role: "",
      experience_years: "", expected_ctc: "", skills: "", summary: "",
    },
  });

  const onSubmit = async (values) => {
    if (!resume) {
      setResumeError("Please upload your resume");
      return;
    }
    try {
      const fd = new FormData();
      Object.entries(values).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("resume", resume);
      await submitCandidate(fd);
      toast.success("You're in our talent pool! We'll be in touch.");
      setSubmitted(true);
      reset();
      setResume(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    }
  };

  return (
    <>
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary">Talent Pool</p>
          <h1 className="mt-3 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight max-w-4xl mx-auto">
            Join our talent pool
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
            Share your profile once — we'll match you to openings across our 100+ corporate clients in
            manufacturing, logistics, retail, IT, hospitality and more.
          </p>
        </div>
      </section>

      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Join Talent Pool" }]} />

      <section data-testid="join-section" className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <h2 className="font-heading font-bold text-3xl text-slate-900 leading-tight">
              Why register with us?
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We've placed over 1000 candidates across India in roles ranging from shop-floor to
              leadership. Whether you're actively searching or open to better opportunities — we
              keep you on our radar.
            </p>
            <ul className="mt-8 space-y-5">
              {PERKS.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-slate-900">{title}</div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl bg-slate-900 text-white p-6">
              <div className="font-heading font-bold text-lg">Looking to hire instead?</div>
              <p className="mt-2 text-sm text-slate-300">If you're an employer, request a proposal from our team.</p>
              <a href="/contact" className="mt-4 inline-flex text-sm font-semibold text-blue-300 hover:text-white">
                Talk to recruitment →
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-slate-50/50 p-6 sm:p-10 shadow-sm">
              {submitted ? (
                <div data-testid="join-success" className="py-14 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-2xl text-slate-900">You're in!</h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Thank you for joining our talent pool. We'll reach out as soon as a suitable role opens up.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="join-another"
                    className="mt-6 text-sm font-semibold text-primary hover:text-primary-hover"
                  >
                    Submit another profile →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} data-testid="join-form" className="space-y-5">
                  <h3 className="font-heading font-bold text-2xl text-slate-900">Candidate Registration</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name *" error={errors.full_name?.message}>
                      <Input data-testid="join-name" placeholder="e.g. Rahul Patil" {...register("full_name")} />
                    </Field>
                    <Field label="Email *" error={errors.email?.message}>
                      <Input data-testid="join-email" type="email" placeholder="you@email.com" {...register("email")} />
                    </Field>
                    <Field label="Phone *" error={errors.phone?.message}>
                      <Input data-testid="join-phone" placeholder="+91 ..." {...register("phone")} />
                    </Field>
                    <Field label="Location">
                      <Input data-testid="join-location" placeholder="City, State" {...register("location")} />
                    </Field>
                    <Field label="Current Role / Designation">
                      <Input data-testid="join-role" placeholder="e.g. Production Supervisor" {...register("current_role")} />
                    </Field>
                    <Field label="Total Experience">
                      <Input data-testid="join-experience" placeholder="e.g. 5 years" {...register("experience_years")} />
                    </Field>
                    <Field label="Expected CTC">
                      <Input data-testid="join-ctc" placeholder="e.g. ₹6 LPA" {...register("expected_ctc")} />
                    </Field>
                    <Field label="Key Skills">
                      <Input data-testid="join-skills" placeholder="Comma separated" {...register("skills")} />
                    </Field>
                  </div>
                  <Field label="Brief Summary">
                    <Textarea data-testid="join-summary" rows={3} placeholder="Tell us about your background, strengths and what you're looking for…" {...register("summary")} />
                  </Field>

                  <div>
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Resume *</Label>
                    <div className="mt-1.5">
                      <ResumeUploader
                        value={resume}
                        onChange={(f, err) => { setResume(f); setResumeError(err); }}
                        error={resumeError}
                        testId="join-resume"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="join-submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white font-semibold px-7 py-6 rounded-lg"
                  >
                    {isSubmitting ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Submitting…</span>
                    ) : "Submit Profile"}
                  </Button>
                  <p className="text-xs text-slate-500">
                    By submitting, you agree to be contacted by our recruitment team about relevant openings.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
